import React, { createContext, useContext, useState } from 'react';

const translations = {
  es: {
    nav: {
      vision: 'Visión',
      problem: 'El Problema',
      solution: 'La Solución',
      howItWorks: 'Cómo Funciona',
      ceoVisor: 'Visor del CEO',
      tinyBoard: 'Tiny Board',
      cases: 'Casos & Resultados',
      methodology: 'Metodología',
      diagnosis: 'Diagnóstico Ejecutivo',
      contact: 'Contacto Privado',
    },
    hero: {
      title: 'El futuro no se improvisa.',
      titleHighlight: 'Se diseña.',
      subtitle: 'Inteligencia Artificial aplicada a la gestión empresarial. Transformamos empresas tradicionales en organizaciones inteligentes, predictivas y estratégicamente automatizadas.',
      tagline: 'No vendemos software. Diseñamos sistemas inteligentes que analizan, conectan, interpretan, proyectan y recomiendan.',
      ctaPrimary: 'Solicitar Diagnóstico Ejecutivo',
      ctaSecondary: 'Ver Cómo Funciona',
    },
    vision: {
      title: 'Nuestra Visión',
      subtitle: 'Desde la Experiencia Real',
      story: 'El fundador ha sido emprendedor, empresario, independiente, director e inversionista. Ha vivido la incertidumbre que enfrentan las pequeñas y grandes empresas.',
      quote: '"El mayor problema no es vender. Es decidir con información incompleta."',
      insight1: 'Como emprendedor entendí que trabajar duro no garantiza estabilidad.',
      insight2: 'Como empresario entendí que el flujo es rey.',
      insight3: 'Como inversionista entendí que la información anticipada vale más que el capital.',
      insight4: 'Como director entendí que decidir con datos incorrectos cuesta años.',
    },
    problem: {
      title: 'El Problema Real',
      subtitle: 'Un CEO moderno no puede trabajar con retrovisores.',
      problems: [
        'Operan con datos fragmentados.',
        'Deciden con información histórica.',
        'Reaccionan tarde.',
        'Dependen de intuición.',
        'No integran factores externos.'
      ],
    },
    questions: {
      title: '¿Te gustaría que tu empresa...?',
      items: [
        '¿Detectara una caída en ventas antes de que ocurra?',
        '¿Anticipara movimientos de la competencia?',
        '¿Supiera cuándo ajustar costos antes de que el flujo se tense?',
        '¿Decidiera con escenarios proyectados, no con intuición?',
        '¿Tuviera una vista clara del futuro probable del negocio?'
      ],
      conclusion: 'Entonces necesitas más que reportes. Necesitas Inteligencia.',
    },
    solution: {
      title: 'La Solución',
      brandName: 'ALLCOM IA TECHNOLOGIES',
      subtitle: 'Arquitectura de Inteligencia Artificial Empresarial Predictiva',
      description: 'No vendemos software. Diseñamos sistemas de IA que:',
      features: ['Analizan', 'Conectan', 'Interpretan', 'Proyectan', 'Recomiendan'],
      differentiator: 'No muestra lo que pasó. Muestra lo que probablemente pasará.',
    },
    howItWorks: {
      title: '¿Cómo Funciona?',
      subtitle: 'Proceso en 6 Niveles',
      steps: [
        {
          title: 'Data Lake Empresarial',
          description: 'Construimos junto a tu empresa un repositorio centralizado de datos. Integramos todas tus fuentes de información en un único lugar seguro.'
        },
        {
          title: 'Conexión en Tiempo Real',
          description: 'Conectamos tu Data Lake con APIs financieras, ERP, CRM, indicadores macroeconómicos y factores del mundo real al instante.'
        },
        {
          title: 'Datos Sintéticos e IA',
          description: 'Generamos bases de datos sintéticas para entrenar modelos de IA que predicen lo que va a pasar en tu negocio.'
        },
        {
          title: 'Visor del CEO™',
          description: 'Todo se presenta en una sola interfaz ejecutiva con predicciones, alertas y recomendaciones automáticas.'
        },
        {
          title: 'Aprendizaje Continuo',
          description: 'El sistema aprende de cada decisión y resultado. Se adapta y mejora constantemente con IA.'
        },
        {
          title: 'C-Suite™',
          description: 'Panel de control y recomendaciones diseñado específicamente para el C-Suite: CEO, CFO, COO, CTO y directores.',
          trademark: true
        }
      ]
    },
    ceoVisor: {
      title: 'Visor del CEO™',
      subtitle: 'Todo a la mano. En tiempo real.',
      description: 'Tu centro de comando ejecutivo. Toda la información crítica de tu empresa consolidada en una sola vista, con predicciones y alertas impulsadas por IA.',
      features: [
        'KPIs en tiempo real',
        'Proyección trimestral y anual',
        'Indicador de riesgo empresarial',
        'Simulación de escenarios',
        'Radar de competencia',
        'Radar económico',
        'Alertas predictivas',
        'Score de estabilidad'
      ],
      integrations: 'Integra ERP, CRM, APIs financieras, datos macroeconómicos y sectoriales.',
    },
    tinyBoard: {
      title: 'Tiny Board Estratégico',
      subtitle: 'No somos solo tecnología. Somos criterio.',
      description: 'Un pequeño directorio compuesto por emprendedores reales, ejecutivos corporativos, analistas estratégicos y especialistas en datos.',
      services: [
        'Revisamos cada caso.',
        'Diseñamos soluciones personalizadas.',
        'Acompañamos decisiones críticas.'
      ],
      tagline: 'Credibilidad humana + Inteligencia tecnológica.',
    },
    pricing: {
      title: 'Inversión',
      subtitle: 'Planes Premium',
      tiers: [
        {
          name: 'INTELLIGENT FOUNDATION',
          implementation: 'USD 12.000 – 25.000',
          monthly: 'USD 800 – 1.500 /mes',
          features: ['Diagnóstico inicial', 'Conexión básica de datos', 'Dashboard ejecutivo', 'Soporte técnico']
        },
        {
          name: 'STRATEGIC INTELLIGENCE',
          implementation: 'USD 35.000 – 90.000',
          monthly: 'USD 2.000 – 5.000 /mes',
          features: ['Todo en Foundation', 'Modelado predictivo avanzado', 'Múltiples integraciones', 'Tiny Board mensual'],
          highlighted: true
        },
        {
          name: 'EXECUTIVE TRANSFORMATION',
          implementation: 'USD 120.000 – 350.000',
          monthly: 'USD 5.000 – 15.000 /mes',
          features: ['Todo en Strategic', 'Transformación completa', 'IA personalizada', 'Tiny Board semanal']
        }
      ],
      diagnostic: {
        title: 'Diagnóstico Ejecutivo Privado',
        price: 'USD 3.500 – 7.000',
        note: 'Descontable si contratan implementación.'
      }
    },
    security: {
      title: 'Seguridad & Compliance',
      items: [
        'Protección de datos empresariales',
        'Encriptación de extremo a extremo',
        'Gobernanza de IA responsable',
        'Cumplimiento normativo local e internacional'
      ]
    },
    contact: {
      title: 'Contacto Privado',
      subtitle: 'El futuro no espera.',
      form: {
        name: 'Nombre completo',
        company: 'Empresa',
        position: 'Cargo',
        email: 'Email corporativo',
        phone: 'Teléfono',
        message: 'Mensaje',
        submit: 'Solicitar Diagnóstico Ejecutivo Privado'
      },
      success: 'Mensaje enviado exitosamente. Nos pondremos en contacto pronto.',
    },
    footer: {
      tagline: 'Arquitectura de Inteligencia Artificial Empresarial Predictiva',
      rights: '© 2024 ALLCOM IA Technologies. Todos los derechos reservados.',
      location: 'Chile, Latinoamérica',
    },
    dashboard: {
      revenue: 'Ingresos Proyectados',
      riskScore: 'Score de Riesgo',
      stability: 'Estabilidad Empresarial',
      competitive: 'Radar Competitivo',
      alerts: 'Alertas Predictivas',
      scenarios: 'Escenarios',
      economic: 'Indicadores Económicos',
      quarterly: 'Proyección Trimestral',
    },
    crew: {
      title: 'Nuestro Equipo',
      subtitle: 'Liderazgo con visión, experiencia y compromiso con la excelencia',
      roles: {
        techLead: 'Líder de Tecnología',
        ceo: 'Founder / CEO',
        engineeringLead: 'Líder de Ingeniería',
      },
      valuesTitle: 'Valores y Principios',
      values: [
        'Innovación constante como motor de transformación empresarial.',
        'Integridad y transparencia en cada decisión y proyecto.',
        'Compromiso absoluto con el éxito de nuestros clientes.',
        'Visión de futuro basada en datos, tecnología y experiencia real.',
        'Excelencia técnica combinada con sensibilidad empresarial.',
      ],
      quote: 'Creemos que el futuro de los negocios se construye hoy, con la combinación perfecta de inteligencia humana y tecnología avanzada. Nuestro equipo une décadas de experiencia real en el mundo empresarial con dominio tecnológico de vanguardia.',
      quoteAuthor: 'Equipo de Liderazgo ALLCOM',
      stats: {
        years: 'Años de experiencia combinada',
        projects: 'Proyectos ejecutados',
        countries: 'Países con presencia',
        commitment: 'Compromiso total',
      },
    }
  },
  en: {
    nav: {
      vision: 'Vision',
      problem: 'The Problem',
      solution: 'The Solution',
      howItWorks: 'How It Works',
      ceoVisor: 'CEO Visor',
      tinyBoard: 'Tiny Board',
      cases: 'Cases & Results',
      methodology: 'Methodology',
      diagnosis: 'Executive Diagnosis',
      contact: 'Private Contact',
    },
    hero: {
      title: 'The future is not improvised.',
      titleHighlight: 'It is designed.',
      subtitle: 'Artificial Intelligence applied to business management. We transform traditional companies into intelligent, predictive, and strategically automated organizations.',
      tagline: 'We don\'t sell software. We design intelligent systems that analyze, connect, interpret, project, and recommend.',
      ctaPrimary: 'Request Executive Diagnosis',
      ctaSecondary: 'See How It Works',
    },
    vision: {
      title: 'Our Vision',
      subtitle: 'From Real Experience',
      story: 'The founder has been an entrepreneur, businessman, independent, director, and investor. He has lived the uncertainty that small and large companies face.',
      quote: '"The biggest problem is not selling. It is deciding with incomplete information."',
      insight1: 'As an entrepreneur, I understood that hard work doesn\'t guarantee stability.',
      insight2: 'As a businessman, I understood that cash flow is king.',
      insight3: 'As an investor, I understood that early information is worth more than capital.',
      insight4: 'As a director, I understood that deciding with wrong data costs years.',
    },
    problem: {
      title: 'The Real Problem',
      subtitle: 'A modern CEO cannot work with rearview mirrors.',
      problems: [
        'They operate with fragmented data.',
        'They decide with historical information.',
        'They react late.',
        'They depend on intuition.',
        'They don\'t integrate external factors.'
      ],
    },
    questions: {
      title: 'Would you like your company to...?',
      items: [
        'Detect a drop in sales before it happens?',
        'Anticipate competitor movements?',
        'Know when to adjust costs before cash flow tightens?',
        'Decide with projected scenarios, not intuition?',
        'Have a clear view of the probable future of the business?'
      ],
      conclusion: 'Then you need more than reports. You need Intelligence.',
    },
    solution: {
      title: 'The Solution',
      brandName: 'ALLCOM IA TECHNOLOGIES',
      subtitle: 'Predictive Business AI Architecture',
      description: 'We don\'t sell software. We design AI systems that:',
      features: ['Analyze', 'Connect', 'Interpret', 'Project', 'Recommend'],
      differentiator: 'It doesn\'t show what happened. It shows what will probably happen.',
    },
    howItWorks: {
      title: 'How Does It Work?',
      subtitle: '5-Level Process',
      steps: [
        {
          title: 'Enterprise Data Lake',
          description: 'We build together with your company a centralized data repository. We integrate all your information sources in one secure place.'
        },
        {
          title: 'Real-Time Connection',
          description: 'We connect your Data Lake with financial APIs, ERP, CRM, macroeconomic indicators, and real-world factors instantly.'
        },
        {
          title: 'Synthetic Data & AI',
          description: 'We generate synthetic databases to train AI models that predict what will happen in your business.'
        },
        {
          title: 'CEO Visor™',
          description: 'Everything is presented in a single executive interface with predictions, alerts, and automatic recommendations.'
        },
        {
          title: 'Continuous Learning',
          description: 'The system learns from every decision and result. It adapts and improves constantly with AI.'
        }
      ]
    },
    ceoVisor: {
      title: 'CEO Visor™',
      subtitle: 'Everything at hand. In real time.',
      description: 'Your executive command center. All critical company information consolidated in a single view, with AI-powered predictions and alerts.',
      features: [
        'Real-time KPIs',
        'Quarterly and annual projection',
        'Business risk indicator',
        'Scenario simulation',
        'Competitive radar',
        'Economic radar',
        'Predictive alerts',
        'Stability score'
      ],
      integrations: 'Integrates ERP, CRM, financial APIs, macroeconomic and sector data.',
    },
    tinyBoard: {
      title: 'Strategic Tiny Board',
      subtitle: 'We are not just technology. We are judgment.',
      description: 'A small board composed of real entrepreneurs, corporate executives, strategic analysts, and data specialists.',
      services: [
        'We review each case.',
        'We design personalized solutions.',
        'We accompany critical decisions.'
      ],
      tagline: 'Human credibility + Technological intelligence.',
    },
    pricing: {
      title: 'Investment',
      subtitle: 'Premium Plans',
      tiers: [
        {
          name: 'INTELLIGENT FOUNDATION',
          implementation: 'USD 12,000 – 25,000',
          monthly: 'USD 800 – 1,500 /month',
          features: ['Initial diagnosis', 'Basic data connection', 'Executive dashboard', 'Technical support']
        },
        {
          name: 'STRATEGIC INTELLIGENCE',
          implementation: 'USD 35,000 – 90,000',
          monthly: 'USD 2,000 – 5,000 /month',
          features: ['Everything in Foundation', 'Advanced predictive modeling', 'Multiple integrations', 'Monthly Tiny Board'],
          highlighted: true
        },
        {
          name: 'EXECUTIVE TRANSFORMATION',
          implementation: 'USD 120,000 – 350,000',
          monthly: 'USD 5,000 – 15,000 /month',
          features: ['Everything in Strategic', 'Complete transformation', 'Personalized AI', 'Weekly Tiny Board']
        }
      ],
      diagnostic: {
        title: 'Private Executive Diagnosis',
        price: 'USD 3,500 – 7,000',
        note: 'Deductible if implementation is contracted.'
      }
    },
    security: {
      title: 'Security & Compliance',
      items: [
        'Business data protection',
        'End-to-end encryption',
        'Responsible AI governance',
        'Local and international regulatory compliance'
      ]
    },
    contact: {
      title: 'Private Contact',
      subtitle: 'The future doesn\'t wait.',
      form: {
        name: 'Full name',
        company: 'Company',
        position: 'Position',
        email: 'Corporate email',
        phone: 'Phone',
        message: 'Message',
        submit: 'Request Private Executive Diagnosis'
      },
      success: 'Message sent successfully. We will contact you soon.',
    },
    footer: {
      tagline: 'Predictive Business AI Architecture',
      rights: '© 2024 ALLCOM IA Technologies. All rights reserved.',
      location: 'Chile, Latin America',
    },
    dashboard: {
      revenue: 'Projected Revenue',
      riskScore: 'Risk Score',
      stability: 'Business Stability',
      competitive: 'Competitive Radar',
      alerts: 'Predictive Alerts',
      scenarios: 'Scenarios',
      economic: 'Economic Indicators',
      quarterly: 'Quarterly Projection',
    },
    crew: {
      title: 'Our Team',
      subtitle: 'Leadership with vision, experience, and commitment to excellence',
      roles: {
        techLead: 'Technology Lead',
        ceo: 'Founder / CEO',
        engineeringLead: 'Engineering Lead',
      },
      valuesTitle: 'Values & Principles',
      values: [
        'Constant innovation as a driver of business transformation.',
        'Integrity and transparency in every decision and project.',
        'Absolute commitment to our clients\' success.',
        'Future vision based on data, technology, and real experience.',
        'Technical excellence combined with business sensitivity.',
      ],
      quote: 'We believe the future of business is built today, with the perfect combination of human intelligence and advanced technology. Our team combines decades of real-world business experience with cutting-edge technological mastery.',
      quoteAuthor: 'ALLCOM Leadership Team',
      stats: {
        years: 'Combined years of experience',
        projects: 'Projects executed',
        countries: 'Countries with presence',
        commitment: 'Total commitment',
      },
    }
  },
  pt: {
    nav: {
      vision: 'Visão',
      problem: 'O Problema',
      solution: 'A Solução',
      howItWorks: 'Como Funciona',
      ceoVisor: 'Visor do CEO',
      tinyBoard: 'Tiny Board',
      cases: 'Casos & Resultados',
      methodology: 'Metodologia',
      diagnosis: 'Diagnóstico Executivo',
      contact: 'Contato Privado',
    },
    hero: {
      title: 'O futuro não se improvisa.',
      titleHighlight: 'Se projeta.',
      subtitle: 'Inteligência Artificial aplicada à gestão empresarial. Transformamos empresas tradicionais em organizações inteligentes, preditivas e estrategicamente automatizadas.',
      tagline: 'Não vendemos software. Projetamos sistemas inteligentes que analisam, conectam, interpretam, projetam e recomendam.',
      ctaPrimary: 'Solicitar Diagnóstico Executivo',
      ctaSecondary: 'Ver Como Funciona',
    },
    vision: {
      title: 'Nossa Visão',
      subtitle: 'Da Experiência Real',
      story: 'O fundador foi empreendedor, empresário, independente, diretor e investidor. Viveu a incerteza que pequenas e grandes empresas enfrentam.',
      quote: '"O maior problema não é vender. É decidir com informação incompleta."',
      insight1: 'Como empreendedor, entendi que trabalhar duro não garante estabilidade.',
      insight2: 'Como empresário, entendi que o fluxo é rei.',
      insight3: 'Como investidor, entendi que informação antecipada vale mais que capital.',
      insight4: 'Como diretor, entendi que decidir com dados errados custa anos.',
    },
    problem: {
      title: 'O Problema Real',
      subtitle: 'Um CEO moderno não pode trabalhar com retrovisores.',
      problems: [
        'Operam com dados fragmentados.',
        'Decidem com informação histórica.',
        'Reagem tarde.',
        'Dependem de intuição.',
        'Não integram fatores externos.'
      ],
    },
    questions: {
      title: 'Você gostaria que sua empresa...?',
      items: [
        'Detectasse uma queda nas vendas antes de acontecer?',
        'Antecipasse movimentos da concorrência?',
        'Soubesse quando ajustar custos antes do fluxo apertar?',
        'Decidisse com cenários projetados, não com intuição?',
        'Tivesse uma visão clara do futuro provável do negócio?'
      ],
      conclusion: 'Então você precisa de mais que relatórios. Você precisa de Inteligência.',
    },
    solution: {
      title: 'A Solução',
      brandName: 'ALLCOM IA TECHNOLOGIES',
      subtitle: 'Arquitetura de Inteligência Artificial Empresarial Preditiva',
      description: 'Não vendemos software. Projetamos sistemas de IA que:',
      features: ['Analisam', 'Conectam', 'Interpretam', 'Projetam', 'Recomendam'],
      differentiator: 'Não mostra o que aconteceu. Mostra o que provavelmente acontecerá.',
    },
    howItWorks: {
      title: 'Como Funciona?',
      subtitle: 'Processo em 5 Níveis',
      steps: [
        {
          title: 'Data Lake Empresarial',
          description: 'Construímos junto à sua empresa um repositório centralizado de dados. Integramos todas as suas fontes de informação em um único lugar seguro.'
        },
        {
          title: 'Conexão em Tempo Real',
          description: 'Conectamos seu Data Lake com APIs financeiras, ERP, CRM, indicadores macroeconômicos e fatores do mundo real instantaneamente.'
        },
        {
          title: 'Dados Sintéticos e IA',
          description: 'Geramos bases de dados sintéticas para treinar modelos de IA que preveem o que vai acontecer no seu negócio.'
        },
        {
          title: 'Visor do CEO™',
          description: 'Tudo é apresentado em uma única interface executiva com previsões, alertas e recomendações automáticas.'
        },
        {
          title: 'Aprendizado Contínuo',
          description: 'O sistema aprende com cada decisão e resultado. Se adapta e melhora constantemente com IA.'
        }
      ]
    },
    ceoVisor: {
      title: 'Visor do CEO™',
      subtitle: 'Tudo à mão. Em tempo real.',
      description: 'Seu centro de comando executivo. Toda a informação crítica da sua empresa consolidada em uma única visão, com previsões e alertas impulsionados por IA.',
      features: [
        'KPIs em tempo real',
        'Projeção trimestral e anual',
        'Indicador de risco empresarial',
        'Simulação de cenários',
        'Radar de concorrência',
        'Radar econômico',
        'Alertas preditivos',
        'Score de estabilidade'
      ],
      integrations: 'Integra ERP, CRM, APIs financeiras, dados macroeconômicos e setoriais.',
    },
    tinyBoard: {
      title: 'Tiny Board Estratégico',
      subtitle: 'Não somos só tecnologia. Somos critério.',
      description: 'Um pequeno conselho composto por empreendedores reais, executivos corporativos, analistas estratégicos e especialistas em dados.',
      services: [
        'Revisamos cada caso.',
        'Projetamos soluções personalizadas.',
        'Acompanhamos decisões críticas.'
      ],
      tagline: 'Credibilidade humana + Inteligência tecnológica.',
    },
    pricing: {
      title: 'Investimento',
      subtitle: 'Planos Premium',
      tiers: [
        {
          name: 'INTELLIGENT FOUNDATION',
          implementation: 'USD 12.000 – 25.000',
          monthly: 'USD 800 – 1.500 /mês',
          features: ['Diagnóstico inicial', 'Conexão básica de dados', 'Dashboard executivo', 'Suporte técnico']
        },
        {
          name: 'STRATEGIC INTELLIGENCE',
          implementation: 'USD 35.000 – 90.000',
          monthly: 'USD 2.000 – 5.000 /mês',
          features: ['Tudo no Foundation', 'Modelagem preditiva avançada', 'Múltiplas integrações', 'Tiny Board mensal'],
          highlighted: true
        },
        {
          name: 'EXECUTIVE TRANSFORMATION',
          implementation: 'USD 120.000 – 350.000',
          monthly: 'USD 5.000 – 15.000 /mês',
          features: ['Tudo no Strategic', 'Transformação completa', 'IA personalizada', 'Tiny Board semanal']
        }
      ],
      diagnostic: {
        title: 'Diagnóstico Executivo Privado',
        price: 'USD 3.500 – 7.000',
        note: 'Dedutível se a implementação for contratada.'
      }
    },
    security: {
      title: 'Segurança & Compliance',
      items: [
        'Proteção de dados empresariais',
        'Criptografia de ponta a ponta',
        'Governança de IA responsável',
        'Conformidade regulatória local e internacional'
      ]
    },
    contact: {
      title: 'Contato Privado',
      subtitle: 'O futuro não espera.',
      form: {
        name: 'Nome completo',
        company: 'Empresa',
        position: 'Cargo',
        email: 'Email corporativo',
        phone: 'Telefone',
        message: 'Mensagem',
        submit: 'Solicitar Diagnóstico Executivo Privado'
      },
      success: 'Mensagem enviada com sucesso. Entraremos em contato em breve.',
    },
    footer: {
      tagline: 'Arquitetura de Inteligência Artificial Empresarial Preditiva',
      rights: '© 2024 ALLCOM IA Technologies. Todos os direitos reservados.',
      location: 'Chile, América Latina',
    },
    dashboard: {
      revenue: 'Receita Projetada',
      riskScore: 'Score de Risco',
      stability: 'Estabilidade Empresarial',
      competitive: 'Radar Competitivo',
      alerts: 'Alertas Preditivos',
      scenarios: 'Cenários',
      economic: 'Indicadores Econômicos',
      quarterly: 'Projeção Trimestral',
    },
    crew: {
      title: 'Nossa Equipe',
      subtitle: 'Liderança com visão, experiência e compromisso com a excelência',
      roles: {
        techLead: 'Líder de Tecnologia',
        ceo: 'Founder / CEO',
        engineeringLead: 'Líder de Engenharia',
      },
      valuesTitle: 'Valores e Princípios',
      values: [
        'Inovação constante como motor de transformação empresarial.',
        'Integridade e transparência em cada decisão e projeto.',
        'Compromisso absoluto com o sucesso dos nossos clientes.',
        'Visão de futuro baseada em dados, tecnologia e experiência real.',
        'Excelência técnica combinada com sensibilidade empresarial.',
      ],
      quote: 'Acreditamos que o futuro dos negócios se constrói hoje, com a combinação perfeita de inteligência humana e tecnologia avançada. Nossa equipe une décadas de experiência real no mundo empresarial com domínio tecnológico de vanguarda.',
      quoteAuthor: 'Equipe de Liderança ALLCOM',
      stats: {
        years: 'Anos de experiência combinada',
        projects: 'Projetos executados',
        countries: 'Países com presença',
        commitment: 'Compromisso total',
      },
    }
  }
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('es');

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, translations }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
